import {
    Button,
    Card,
    CardContent,
    CardHeader,
    colors,
    FormControlLabel,
    Grid,
    makeStyles,
    Switch,
    TextField,
    Theme,
    Typography
} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import useRouter from 'use-react-router';
import validate from 'validate.js';
import {Can} from '../../../components/Can';
import ConfirmDelete from '../../../components/ConfirmDelete';
import ConfirmBasic from '../../../components/ConfirmBasic';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import anyLoading from '../../../utils/anyLoading';
import {getHandleFieldChange, getHasError} from '../../../utils/formHelpers';
import {RegionType, sortedCountries} from '../../../utils/countries';
import {InternationalPhoneInput} from '../../../components/PhoneInput/PhoneInput';
import updateBillingParty, {UpdateBillingPartyInput} from '../BillingPartyDetails/updateBillingParty.api';
import deleteBillingPartyMember from './deleteBillingPartyMember.api';
import {BillingPartyMember} from '../../../graphql/billing_party.datatypes';
import AutomaticPaymentCheckoutFrame from '../../Payments/AutomaticPaymentCheckoutFrame';
import ManageAutomaticPaymentModal from '../../Payments/ManageAutomaticPaymentModal/ManageAutomaticPaymentModal';
import deleteAutomaticPayment from "../../Payments/ManageAutomaticPaymentModal/deleteAutomaticPayment.api";
import {AutomaticPayment} from "../../../graphql/payments.datatypes";
import { FeatureContext } from '../../../App';
import { FeatureFlagSlug } from '../../../graphql/features';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        marginTop: theme.spacing(3),
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    card: {},
    saveButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    },
    deleteButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: theme.palette.error.light,
        '&:hover': {
            backgroundColor: theme.palette.error.main
        }
    },
    cloneButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[400],
        '&:hover': {
            backgroundColor: colors.green[700]
        }
    },
    errorIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.error.main,
    },
    marginBottom: {
        paddingLeft: theme.spacing(1),
        marginBottom: theme.spacing(3),
    },
    tabs: {
        marginTop: theme.spacing(3)
    },
    textField: {
        width: '100%',
    },
}));


export const BillingPartyMemberEdit = (props: { canEdit: boolean, isAdmin: boolean, billingPartyMember: BillingPartyMember, setBillingPartyMember?: (billingPartyMember: BillingPartyMember | null) => void }) => {
    const classes = useStyles();
    const {billingPartyMember, setBillingPartyMember, canEdit, isAdmin} = props;
    const isNewMember = billingPartyMember.id === -1;
    const {history} = useRouter();
    const [open, setOpen] = React.useState(false);
    const {enqueueSnackbar} = useSnackbar();
    const [sendUpdateBillingParty, updateData] = updateBillingParty();
    const [sendDeleteBillingParty, deleteData] = deleteBillingPartyMember();
    const [regions, setRegions] = useState<RegionType[]>([]);
    const [memberHasAutomaticPayment, setMemberHasAutomaticPayment] = useState(billingPartyMember.hasAutomaticPayment);
    const [sendDeleteAutomaticPayment, deleteAutomaticPaymentData] = deleteAutomaticPayment();
    const [openAutoPaymentConfirmation, setOpenAutoPaymentConfirmation] = useState(false);
    // @ts-ignore
    const automaticPayment: AutomaticPayment = billingPartyMember.automaticPayment;
    const isPrimaryPayingMember = billingPartyMember.primary_paying_member

    // Feature flags
    const features = React.useContext(FeatureContext);
    const automaticPaymentsFeature = features.find(f => f.slug === FeatureFlagSlug.AUTOMATIC_PAYMENTS)
    const automaticPaymentsEnabled = automaticPaymentsFeature && automaticPaymentsFeature.enabled;

    //1. Set form and error state

    const [formState, setFormState] = useState({
        ...billingPartyMember,
        address_line_2: billingPartyMember.address_line_2 ? billingPartyMember.address_line_2 : '',
        primary_phone_code: billingPartyMember.primary_phone_code ? billingPartyMember.primary_phone_code : '+1',
        secondary_phone_code: billingPartyMember.secondary_phone_code ? billingPartyMember.secondary_phone_code : '+1',
        notes: billingPartyMember.notes ? billingPartyMember.notes : '',
    });
    const loadingData = anyLoading(updateData, deleteData);
    const [errors, setErrors] = useState({
        isValid: false,
        errors: {
            name: false,
            description: false,
            start_date: false,
            end_date: false,
            admissions_cutoff: false,
        }
    });

    useEffect(() => {
        setFormState({
            ...billingPartyMember,
            address_line_2: billingPartyMember.address_line_2 ? billingPartyMember.address_line_2 : '',
            primary_phone_code: billingPartyMember.primary_phone_code ? billingPartyMember.primary_phone_code : '+1',
            secondary_phone_code: billingPartyMember.secondary_phone_code ? billingPartyMember.secondary_phone_code : '+1',
            notes: billingPartyMember.notes ? billingPartyMember.notes : '',
        });
    }, [billingPartyMember]);

    //2. Setup helper functions for form
    const handleFieldChange = getHandleFieldChange(setFormState);
    const hasError = getHasError(errors);

    //3. setup validation and error hook
    const validateSchema: any = {
        first_name: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        last_name: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        email: {
            presence: {allowEmpty: false, message: 'is required'},
            email: true,
            length: {
                maximum: 64
            }
        },
        address_line_1: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        city: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        state: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        zipcode: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        country: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        primary_phone: {
            presence: {allowEmpty: false, message: 'is required'},
            phone: true,
        },
    };

    useEffect(() => {
        const newErrors = validate(formState, validateSchema);
        const isValid = !newErrors;
        const selectedCountry = sortedCountries.find(country => country.countryShortCode === formState.country);

        setErrors(state => ({
            ...state,
            isValid,
            errors: newErrors || {}
        }));

        if (selectedCountry) {
            if (regions !== selectedCountry.regions) {
                setRegions(selectedCountry.regions);
            }
        }

    }, [formState]);

    //4. Handle save form
    const onSaveClick = () => {
        if (isNewMember) {
            const sanitisedData: UpdateBillingPartyInput = {
                data: {
                    ...{
                        id: billingPartyMember.billing_party_id!,
                        payingMembers: {
                            create: [
                                {
                                    ...formState,
                                    billing_party_id: undefined,
                                    __typename: undefined,
                                    id: undefined,
                                    billingParty: undefined,
                                    user: undefined,
                                    user_id: undefined,
                                    payments: undefined
                                },
                            ]
                        },
                    },
                }
            };

            sendUpdateBillingParty({variables: sanitisedData})
                .then((response) => {
                    if (setBillingPartyMember) {
                        const newMember = response.data &&
                            response.data!.updateBillingParty.payingMembers &&
                            response.data.updateBillingParty.payingMembers.find(member => member.email === formState.email);

                        if (newMember) {
                            setBillingPartyMember(newMember);
                        } else {
                            setBillingPartyMember(null);
                        }
                    }
                    enqueueSnackbar('Booking Party Member Saved.', {variant: 'success'});
                })
                .catch(() => {
                    enqueueSnackbar('Save Booking Party Member Failed!', {variant: 'error'});
                });
        } else {
            const sanitisedData: UpdateBillingPartyInput = {
                data: {
                    ...{
                        id: billingPartyMember.billing_party_id!,
                        payingMembers: {
                            update: [
                                {
                                    ...formState,
                                    billing_party_id: undefined,
                                    __typename: undefined,
                                    billingParty: undefined,
                                    user: undefined,
                                    user_id: undefined,
                                    payments: undefined,
                                    automaticPayment: undefined,
                                    hasAutomaticPayment: undefined
                                },
                            ]
                        },
                    },
                }
            };

            sendUpdateBillingParty({variables: sanitisedData})
                .then(() => {
                    enqueueSnackbar('Booking Party Updated.', {variant: 'success'});
                })
                .catch(() => {
                    enqueueSnackbar('Booking Party Update Failed!', {variant: 'error'});
                });
        }
    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleDelete = () => {
        sendDeleteBillingParty({variables: {id: formState.id}})
            .then(() => {
                if (setBillingPartyMember) setBillingPartyMember(null);
                enqueueSnackbar('Booking Party Deleted.', {variant: 'success'});
            })
            .catch(() => {
                enqueueSnackbar('Deleted Booking Party Failed!', {variant: 'error'});
            });

        handleClose();
    };

    const [openPaymentModal, setOpenPaymentModal] = useState(false);
    const [openPaymentManagementModal, setOpenPaymentManagementModal] = useState(false);




    const handleAutomaticPaymentsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        const backupCheckedValue = !isChecked; // the value before the change

        if (isChecked) {
            handleOpenPaymentModal(event.target.checked);
            return;
        }

        if (backupCheckedValue && !isChecked) {
            setOpenAutoPaymentConfirmation(true);
            return;
        }
    };

    const handleOpenPaymentModal = (checked: boolean) => {
        setOpenPaymentModal(true);
        setMemberHasAutomaticPayment(checked);
    };

    const handleRemoveAutomaticPayment = () => {
        setOpenAutoPaymentConfirmation(false);
        setOpenPaymentModal(false);
        setMemberHasAutomaticPayment(false)

        if (!automaticPayment || !automaticPayment.id) {
            enqueueSnackbar('No automatic payment to delete.', {variant: 'warning'});
            return;
        }

        sendDeleteAutomaticPayment({variables: {id: automaticPayment.id}})
            .then((result) => {
                enqueueSnackbar('Automatic Payment Deleted.', {variant: 'success'});
            })
            .catch((error) => {
                enqueueSnackbar('Delete Automatic Payment Failed!', {variant: 'error'});
            });
    };

    const handleOpenPaymentManagementModal = () => {
        setOpenPaymentManagementModal(true)
    }


    return (
        <form>
            <LoadingOverlay loading={loadingData.loading}>

                <Card className={classes.container}>
                    <CardHeader title="Edit Billing Party" action={[
                        isAdmin && billingPartyMember.user &&
                        <Button key={1} onClick={() => history.push('/users/' + billingPartyMember.user!.id)}
                                variant="contained">Go To Linked User
                        </Button>,
                    ]}/>
                    <CardContent className={classes.card}>
                        <Grid container spacing={3}>
                            {billingPartyMember.primary_paying_member && (
                                <Grid item md={12}>
                                    <Typography variant={'h3'}>Primary Paying Member</Typography>
                                    {isAdmin && automaticPaymentsEnabled && (
                                        <FormControlLabel
                                            style={{
                                                cursor: 'default'
                                            }}
                                            control={
                                                <Switch
                                                    checked={memberHasAutomaticPayment}
                                                    onChange={() => {}}
                                                    name="automaticPayment"
                                                    color="primary"
                                                    disabled={true}
                                                    style={{
                                                        color: '#3f51b5'
                                                    }}
                                                />
                                            }
                                            label="Enrolled in Automatic Payments"
                                            disabled={true}
                                        />
                                    )}
                                </Grid>
                            )}
                            <Grid item xs={12} style={{display: 'flex'}}>
                                <Grid container spacing={3}>
                                    <Grid item md={4} xs={6}>
                                        <TextField
                                            fullWidth
                                            label="First Name"
                                            name="first_name"
                                            onChange={handleFieldChange}
                                            {...hasError('first_name')}
                                            value={formState.first_name}
                                            disabled={!canEdit}
                                        />
                                    </Grid>
                                    <Grid item md={4} xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Last Name"
                                            name="last_name"
                                            onChange={handleFieldChange}
                                            {...hasError('last_name')}
                                            value={formState.last_name}
                                            disabled={!canEdit}
                                        />
                                    </Grid>
                                    <Grid item md={4} xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            name="email"
                                            onChange={handleFieldChange}
                                            {...hasError('email')}
                                            value={formState.email}
                                            disabled={!canEdit}
                                        />
                                    </Grid>
                                    <Grid item md={4} xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Billing Address Line 1"
                                            name="address_line_1"
                                            onChange={handleFieldChange}
                                            {...hasError('address_line_1')}
                                            value={formState.address_line_1}
                                            disabled={!canEdit}
                                        />
                                    </Grid>
                                    <Grid item md={4} xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Address Line 2"
                                            name="address_line_2"
                                            onChange={handleFieldChange}
                                            {...hasError('address_line_2')}
                                            value={formState.address_line_2}
                                            disabled={!canEdit}
                                        />
                                    </Grid>
                                    <Grid item md={4} xs={6}>
                                        <TextField
                                            fullWidth
                                            label="City"
                                            name="city"
                                            onChange={handleFieldChange}
                                            {...hasError('city')}
                                            value={formState.city}
                                            disabled={!canEdit}
                                        />
                                    </Grid>

                                    <Grid item md={4} xs={6}>

                                        <TextField
                                            {...hasError('country')}
                                            fullWidth
                                            label="Country"
                                            name="country"
                                            onChange={handleFieldChange}
                                            select
                                            // eslint-disable-next-line react/jsx-sort-props
                                            SelectProps={{native: true}}
                                            value={formState.country}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            disabled={!canEdit}
                                        >
                                            <option value="">please select</option>
                                            {sortedCountries.map((country) => (
                                                <option key={country.countryShortCode} value={country.countryShortCode}>
                                                    {country.countryName}
                                                </option>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item md={4} xs={6}>
                                        <TextField
                                            {...hasError('state')}
                                            fullWidth
                                            label="State"
                                            name="state"
                                            onChange={handleFieldChange}
                                            select
                                            // eslint-disable-next-line react/jsx-sort-props
                                            SelectProps={{native: true}}
                                            value={formState.state}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            disabled={!canEdit}
                                        >
                                            <option value="">please select</option>
                                            {regions.map((region) => (
                                                <option key={region.shortCode} value={region.shortCode}>
                                                    {region.name}
                                                </option>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item md={4} xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Zipcode"
                                            name="zipcode"
                                            onChange={handleFieldChange}
                                            {...hasError('zipcode')}
                                            value={formState.zipcode}
                                            disabled={!canEdit}
                                        />
                                    </Grid>

                                    <Grid item md={4} xs={6}>

                                        <InternationalPhoneInput
                                            className={classes.textField}
                                            fullWidth
                                            {...hasError('primary_phone')}
                                            notched="true"
                                            label={'Primary Phone #'}
                                            name="primary_phone"
                                            type="text"
                                            value={formState.primary_phone}
                                            codeValue={formState.primary_phone_code}
                                            onChange={handleFieldChange}
                                            onCodeChange={handleFieldChange}
                                            disabled={!canEdit}
                                        />
                                    </Grid>

                                    <Grid item md={4} xs={6}>

                                        <InternationalPhoneInput
                                            className={classes.textField}
                                            fullWidth
                                            {...hasError('secondary_phone')}
                                            notched="true"
                                            label={'Secondary Phone #'}
                                            name="secondary_phone"
                                            type="text"
                                            value={formState.secondary_phone}
                                            codeValue={formState.secondary_phone_code}
                                            onChange={handleFieldChange}
                                            onCodeChange={handleFieldChange}
                                            disabled={!canEdit}
                                        />
                                    </Grid>
                                </Grid>

                            </Grid>

                            {/* {isAdmin && (
                                <Grid item xs={12}>
                                    <TextField
                                        {...hasError('notes')}
                                        fullWidth
                                        label="Notes"
                                        variant="outlined"
                                        name="notes"
                                        multiline
                                        value={formState.notes}
                                        onChange={handleFieldChange}
                                        disabled={!canEdit}
                                    />
                                </Grid>
                            )} */}

                            {isAdmin && (
                                <Grid item xs={12}>
                                    <Grid component="label" container alignItems="center" spacing={1}>
                                        <Grid item>
                                            <Switch
                                                checked={formState.is_group_leader}
                                                color="secondary"
                                                edge="start"
                                                name="is_group_leader"
                                                onChange={handleFieldChange}
                                                value={formState.is_group_leader}
                                            />
                                        </Grid>
                                        <Grid item><Typography variant="h5">Allow payments to be applied to any group
                                            members?</Typography></Grid>
                                    </Grid>
                                </Grid>
                            )}

                            {!isAdmin && isPrimaryPayingMember && automaticPaymentsEnabled && (
                                <Grid item xs={12}>
                                    <Grid component="label" container alignItems="center" spacing={1}>
                                        <Grid item>
                                            <Switch
                                                checked={memberHasAutomaticPayment}
                                                color="secondary"
                                                edge="start"
                                                name="is_automatic_payments"
                                                onChange={canEdit ? handleAutomaticPaymentsChange : () => {}}
                                                value={false}
                                                disabled={!canEdit}
                                            />
                                        </Grid>
                                        <Grid item><Typography variant="h5">Automatic Payments</Typography></Grid>
                                        {billingPartyMember.hasAutomaticPayment && (
                                            <Grid item>
                                                <Button
                                                    color="primary"
                                                    variant="text"
                                                    onClick={handleOpenPaymentManagementModal}
                                                >Manage Automatic Payments</Button>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                            )}
                            {openPaymentModal && (
                                <AutomaticPaymentCheckoutFrame
                                    onClose={() => {
                                        setOpenPaymentModal(false);
                                        setMemberHasAutomaticPayment(false);
                                    }}
                                    onSuccess={() => {
                                        setOpenPaymentModal(false);
                                        setMemberHasAutomaticPayment(true);
                                    }}
                                    open={openPaymentModal}
                                    billingPartyMemberId={billingPartyMember.id }
                                />
                            )}
                            {openPaymentManagementModal && billingPartyMember.automaticPayment && (
                                <ManageAutomaticPaymentModal
                                    onClose={() => setOpenPaymentManagementModal(false)}
                                    onPaymentDelete={() => setMemberHasAutomaticPayment(false)}
                                    open={openPaymentManagementModal}
                                    billingPartyMember={billingPartyMember}
                                    automaticPayment={billingPartyMember.automaticPayment}
                                />
                            )}
                            <Grid item xs={12}>
                                <Button className={classes.saveButton} onClick={onSaveClick} variant="contained"
                                        disabled={!errors.isValid || !canEdit}>Save</Button>
                                <Can permission="groups.delete">
                                    <Button onClick={handleClickOpen} className={classes.deleteButton}
                                            variant="contained"
                                            disabled={isNewMember || !canEdit || billingPartyMember.primary_paying_member}>
                                        Delete
                                    </Button>
                                </Can>
                            </Grid>

                        </Grid>
                    </CardContent>
                </Card>

            </LoadingOverlay>

            <ConfirmDelete onClose={handleClose} onDeleteClick={handleDelete} open={open}/>
            <ConfirmBasic
                onClose={()=> setOpenAutoPaymentConfirmation(false)}
                onConfirmClick={handleRemoveAutomaticPayment}
                open={openAutoPaymentConfirmation}
                title={'Remove Automatic Payment'}
                message={'Are you sure you want to remove automatic payment?'}
            />
        </form>
    );
};

export default BillingPartyMemberEdit;
